/**
 * Retrouver les commandes d'un résident, avec la même règle que la liste.
 *
 * La liste (`Manager/Bewoners`) rattache une commande à un résident en passant
 * par l'annuaire : elle indexe `woshUserId -> clé annuaire`, puis regroupe sous
 * cette clé. La fiche, elle, comparait les champs bruts de la commande à
 * l'identifiant de l'URL — sans jamais consulter l'annuaire.
 *
 * Les deux vues divergeaient donc dès qu'une commande ne portait pas
 * littéralement l'identifiant sous lequel la liste l'avait rangée : la ligne
 * annonçait « 3 commandes », la fiche s'ouvrait sur « 0 bestellingen » et un
 * historique vide. Ce n'est pas un affichage approximatif, c'est une vue qui
 * contredit celle depuis laquelle on a cliqué.
 */

const asKey = (value) =>
  value == null || value === "" ? null : String(value);

/**
 * Tous les identifiants sous lesquels ce résident peut apparaître : celui de
 * l'URL, plus ceux que l'annuaire lui connaît.
 */
export const residentAliases = (directory, residentId) => {
  const wanted = asKey(residentId);
  const entry = (directory || []).find(
    (candidate) =>
      asKey(candidate?.id) === wanted || asKey(candidate?.woshUserId) === wanted
  );

  return {
    ids: new Set([wanted, asKey(entry?.id), asKey(entry?.woshUserId)].filter(Boolean)),
    // Dernier recours seulement — voir plus bas.
    name: asKey(entry?.name),
  };
};

/**
 * Les commandes du résident, les plus récentes d'abord.
 *
 * Le rattachement par nom est un dernier recours, et uniquement pour les
 * commandes qui ne portent aucun identifiant : c'est le seul cas où la liste
 * elle-même se rabat sur le nom pour regrouper. L'appliquer plus largement
 * fusionnerait deux homonymes.
 */
export const selectResidentOrders = (orders, directory, residentId) => {
  const { ids, name } = residentAliases(directory, residentId);

  return (orders || [])
    .filter((order) => {
      const byId =
        ids.has(asKey(order?.residentId)) || ids.has(asKey(order?.woshUserId));
      if (byId) return true;

      const anonymous = asKey(order?.residentId) == null && asKey(order?.woshUserId) == null;
      return Boolean(anonymous && name && asKey(order?.residentName) === name);
    })
    .sort((a, b) => (b.pickupDate || 0) - (a.pickupDate || 0));
};
